/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { Redirect } from 'react-router-dom';
import styled from '@emotion/styled';
import { get, compact } from 'lodash/fp';
import lineUpSelector from 'app/selectors/lineUpSelector';
import { IN_PROGRESS } from 'app/constants/challengeStatusTypes';
import { fetchChallenge, resetChallenge } from 'app/actions/challengeActions';
import FriendsLoader from 'app/components/friends/common/FriendsLoader';
import CloseableInstructions from 'app/components/commons/CloseableInstructions';
import LiveChallenge from 'app/components/challenge/LiveChallenge';
import OpenChallenge from 'app/components/challenge/OpenChallenge';
import Page from 'app/containers/parts/Page';
import TrainingChallengeNotice from 'app/components/challenge/TrainingChallengeNotice';
import t from 'app/utils/translate';
import { isLoggedIn } from '../api/auth';
import NotFound from './static/NotFound';
import AdImageLink from '../components/commons/AdImageLink';

type Props = {
  challengeAd?: Object,
  challengeDataRequested: boolean,
  challengeId: string, // eslint-disable-line react/no-unused-prop-types
  entryRoute?: string,
  entryIdsToUpdate: Array<string>,
  fetchingUserData: boolean,
  hasInitialised: boolean,
  isLoading: boolean,
  isPxcEnabled: boolean,
  isRapidfire: boolean,
  isSignedIn: boolean,
  isSigningIn: boolean,
  isTrainingChallenge: boolean,
  loadChallenge: () => void,
  location: Object,
  name: ?string,
  onReset: () => void,
  responseStatusCode: number,
  statusId: number
};

const LoadingWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
  width: '100%'
});

const ChallengeAdWrapper = styled.div({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  margin: '2px 0'
});

class Challenge extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    challengeAd: undefined,
    entryRoute: undefined
  };

  componentDidMount() {
    const { isSigningIn, loadChallenge } = this.props;

    // if we have a login token it will be sent with the challenge request
    if (!isSigningIn || isLoggedIn()) {
      loadChallenge();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      challengeDataRequested,
      challengeId,
      entryRoute,
      hasInitialised,
      isSignedIn,
      isSigningIn,
      loadChallenge
    } = this.props;

    if (
      (!challengeDataRequested && !isSigningIn) ||
      ((challengeId !== prevProps.challengeId || isSignedIn !== prevProps.isSignedIn) &&
        hasInitialised) ||
      entryRoute !== prevProps.entryRoute
    ) {
      // the challenge can change without unmounting by clicking on
      // a live challenge from the right hand side bar.
      loadChallenge();
    }
  }

  componentWillUnmount() {
    const { onReset } = this.props;
    onReset();
  }

  render() {
    const {
      challengeAd,
      challengeId,
      entryIdsToUpdate,
      fetchingUserData,
      hasInitialised,
      isLoading,
      isPxcEnabled,
      isRapidfire,
      isTrainingChallenge,
      location: { pathname },
      name,
      responseStatusCode,
      statusId
    } = this.props;

    if (isRapidfire && hasInitialised && !isLoading) {
      return <Redirect to={`/rapidfire${pathname}`} />;
    }

    if (responseStatusCode === 404) {
      return <NotFound />;
    }

    // statusId will be 0 when challenge is loading
    const isOpen = !!statusId && statusId < IN_PROGRESS;

    return (
      <Page showAd>
        <Helmet title={name ? `${name} - ${t('Challenge')}` : undefined} />
        {isTrainingChallenge ? (
          <TrainingChallengeNotice />
        ) : (
          isOpen && (
            <CloseableInstructions
              storageKey="HIDE_CHALLENGE_INSTRUCTIONS"
              title={t('Create your lineup')}
            >
              {t('Select your players and then enter the challenge by submitting your lineup.')}
            </CloseableInstructions>
          )
        )}
        {challengeAd && challengeAd.enabled && (
          <ChallengeAdWrapper>
            <AdImageLink ad={challengeAd} />
          </ChallengeAdWrapper>
        )}
        {!hasInitialised && (
          <LoadingWrapper>
            <FriendsLoader>
              {isLoading || fetchingUserData
                ? t('Fetching the challenge, just a moment...')
                : t('Missing challenge data')}
            </FriendsLoader>
          </LoadingWrapper>
        )}
        {hasInitialised &&
          (isOpen ? (
            <OpenChallenge
              key={challengeId}
              entryIdsToUpdate={entryIdsToUpdate}
              isTrainingChallenge={isTrainingChallenge}
              isPxcEnabled={isPxcEnabled}
            />
          ) : (
            <LiveChallenge key={challengeId} />
          ))}
      </Page>
    );
  }
}

function mapStateToProps(state, { match, location }) {
  const { statusId } = state.challenge;
  return {
    challengeAd: get('settings.kayoAds.challengeAd', state),
    challengeDataRequested: !!state.challenge.id,
    challengeId: match.params.id,
    entryRoute: match.params.entryIds,
    fetchingUserData: state.user.fetchingUserData,
    hasInitialised: state.challenge.hasInitialised && state.challenge.id === match.params.id,
    isRapidfire: state.challenge.isRapidfire,
    isLoading: state.challenge.isLoading,
    isSignedIn: state.user.isSignedIn,
    isSigningIn: state.user.isSigningIn,
    isTrainingChallenge: state.challenge.isTrainingChallenge,
    isPxcEnabled: !!state.settings.enablePxc,
    lineUpSize: lineUpSelector(state).length,
    location,
    entryIdsToUpdate:
      match.params.entryIds && statusId < IN_PROGRESS
        ? match.params.entryIds.split('-')
        : [],
    name: state.challenge.name,
    opponentEntryId: state.entry.opponentEntryId,
    pollTimestamp: state.challenge.pollTimestamp,
    requiredSelections: state.challenge.playerQuota + state.challenge.teamQuota,
    responseStatusCode: state.challenge.responseStatusCode,
    sharingMessages: state.challenge.sharingMessages,
    statusId
  };
}

function mapDispatchToProps(dispatch, { match }) {
  const { id, entryIds } = match.params;
  const entryIdsArray = entryIds ? entryIds.split('-') : [];

  return {
    loadChallenge() {
      dispatch(
        fetchChallenge(id, {
          force: true,
          userEntryId: entryIdsArray[0]
        })
      );
    },
    onReset() {
      dispatch(resetChallenge());
    }
  };
}

const connector: Connector<{ match: Object }, Props> = connect(mapStateToProps, mapDispatchToProps);

export default connector(Challenge);
